/**
 * ParticipantOutcomesTab — Phase 4.5 frontend
 *
 * Full outcomes report UI for the Participant Chart. Lives behind the
 * `goal_outcomes_reporting_enabled` flag (parent component decides whether
 * to mount it). Pulls from
 *   GET /api/participants/{id}/outcomes-report?from=&to=
 * and renders:
 *   - top summary tiles
 *   - date range filter
 *   - outcomes history table (goal, type, achievement %, recorded date,
 *     recorded by, notes)
 *   - empty state when no outcomes exist
 *   - print-friendly layout (window.print uses browser native styling)
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TrinitiDatePicker } from '@/components/triniti-datetime';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Award, Printer, RefreshCw, FileDown } from 'lucide-react';
const TYPE_BADGE_TONE = {
  PERCENT: 'bg-blue-100 text-blue-700',
  MILESTONE: 'bg-emerald-100 text-emerald-700',
  CUSTOM: 'bg-slate-100 text-slate-700',
  GAS: 'bg-purple-100 text-purple-700',
  COPM: 'bg-amber-100 text-amber-700',
};

function fmtDate(iso) {
  if (!iso) return '-';
  return String(iso).slice(0, 10);
}

function summaryRow(label, value, testid) {
  return (
    <div data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-xl font-semibold">{value}</p>
    </div>
  );
}

export function ParticipantOutcomesTab({ participantId, goals: goalsProp = null }) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ outcomes: [], summary: null });
  const [editRow, setEditRow] = useState(null);
  const [editNote, setEditNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [fetchedGoals, setFetchedGoals] = useState([]);

  // If goals weren't supplied by the parent, fetch them so we can resolve
  // goal_id -> title in the history table.
  useEffect(() => {
    if (goalsProp && goalsProp.length > 0) return;
    if (!participantId) return;
    let cancelled = false;
    api.get(`/goals/by-participant/${participantId}?pageSize=100`)
      .then(r => {
        if (cancelled) return;
        setFetchedGoals(r.data.items || r.data.data || []);
      })
      .catch(() => { if (!cancelled) setFetchedGoals([]); });
    return () => { cancelled = true; };
  }, [participantId, goalsProp]);

  const goals = (goalsProp && goalsProp.length > 0) ? goalsProp : fetchedGoals;

  const goalLookup = useMemo(() => {
    const m = {};
    (goals || []).forEach(g => { m[g.id] = g; });
    return m;
  }, [goals]);

  const load = useCallback(async () => {
    if (!participantId) return;
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams();
      if (from) params.append('from', from);
      if (to) params.append('to', to);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const r = await api.get(`/participants/${participantId}/outcomes-report${qs}`);
      const payload = r.data.data || {};
      setData({
        outcomes: payload.outcomes || [],
        summary: payload.summary || null,
      });
    } catch (e) {
      setError(e?.response?.status === 404
        ? 'Outcomes & Reporting not enabled.'
        : 'Unable to load outcomes.');
      setData({ outcomes: [], summary: null });
    } finally {
      setLoading(false);
    }
  }, [participantId, from, to]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const header = ['Recorded At', 'Goal', 'Measurement', 'Achievement %', 'Recorded By', 'Note'];
    const rows = data.outcomes.map(o => [
      fmtDate(o.recorded_at),
      (goalLookup[o.goal_id]?.title || o.goal_id || '').replace(/"/g, '""'),
      o.measurement_type || '',
      (o.achievement_pct ?? '').toString(),
      o.recorded_by_user_id || '',
      (o.note || '').replace(/"/g, '""').replace(/\n/g, ' '),
    ]);
    const csv = [header, ...rows]
      .map(r => r.map(c => `"${c}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `outcomes_${participantId}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4 print:space-y-2" data-testid="participant-outcomes-tab">
      {/* Header & filters */}
      <div className="flex flex-wrap items-end justify-between gap-3 print:hidden">
        <div>
          <h3 className="font-semibold text-base flex items-center gap-2">
            <Award className="h-4 w-4 text-primary" /> Outcomes Report
          </h3>
          <p className="text-xs text-muted-foreground">
            Goal achievement history for this participant.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">From</Label>
            <TrinitiDatePicker value={from} onChange={v => setFrom(v)}
                   className="h-8 text-sm w-36" testId="outcomes-filter-from" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">To</Label>
            <TrinitiDatePicker value={to} onChange={v => setTo(v)}
                   className="h-8 text-sm w-36" testId="outcomes-filter-to" />
          </div>
          <Button size="sm" variant="outline" onClick={load} data-testid="outcomes-refresh">
            <RefreshCw className="h-3 w-3 mr-1" /> Refresh
          </Button>
          <Button size="sm" variant="outline" onClick={() => window.print()} data-testid="outcomes-print">
            <Printer className="h-3 w-3 mr-1" /> Print
          </Button>
          <Button size="sm" variant="outline" onClick={exportCsv}
                  disabled={data.outcomes.length === 0} data-testid="outcomes-export-csv">
            <FileDown className="h-3 w-3 mr-1" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Summary tiles */}
      <Card data-testid="outcomes-tab-summary">
        <CardContent className="p-4">
          {loading ? (
            <p className="text-xs text-muted-foreground">Loading…</p>
          ) : error ? (
            <p className="text-xs text-red-600" data-testid="outcomes-tab-error">{error}</p>
          ) : data.summary && data.summary.total_outcomes > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {summaryRow('Total recorded', data.summary.total_outcomes, 'outcomes-tab-total')}
              {summaryRow('Avg achievement', `${data.summary.achievement_avg_pct}%`, 'outcomes-tab-avg')}
              {summaryRow('Achieved rate', `${data.summary.achievement_rate_pct}%`, 'outcomes-tab-rate')}
              <div>
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">By type</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {Object.entries(data.summary.by_measurement_type || {}).map(([k, v]) => (
                    <span key={k} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] ${TYPE_BADGE_TONE[k] || 'bg-muted'}`}>
                      {k}: {v}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground" data-testid="outcomes-tab-empty">
              No outcomes recorded for the selected period.
            </p>
          )}
        </CardContent>
      </Card>

      {/* History table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="outcomes-history-table">
              <thead className="bg-muted/40">
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2">Recorded</th>
                  <th className="px-3 py-2">Goal</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2 text-right">Achievement</th>
                  <th className="px-3 py-2">Recorded by</th>
                  <th className="px-3 py-2">Note</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan="7" className="px-3 py-6 text-center text-xs text-muted-foreground">Loading outcomes…</td></tr>
                )}
                {!loading && !error && data.outcomes.length === 0 && (
                  <tr><td colSpan="7" className="px-3 py-6 text-center text-xs text-muted-foreground"
                          data-testid="outcomes-history-empty">
                    No outcomes match the current filters.
                  </td></tr>
                )}
                {!loading && data.outcomes.map(o => {
                  const goal = goalLookup[o.goal_id];
                  return (
                    <tr key={o.id} className="border-t hover:bg-muted/20"
                        data-testid={`outcomes-row-${o.id}`}>
                      <td className="px-3 py-2 whitespace-nowrap">{fmtDate(o.recorded_at)}</td>
                      <td className="px-3 py-2">
                        <div className="font-medium truncate max-w-[260px]">
                          {goal?.title || <span className="text-muted-foreground">{o.goal_id}</span>}
                        </div>
                        {goal?.goal_no && (
                          <div className="text-[10px] text-muted-foreground font-mono">{goal.goal_no}</div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <Badge className={`text-[10px] ${TYPE_BADGE_TONE[o.measurement_type] || ''}`}>
                          {o.measurement_type}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-right font-semibold">
                        {o.achievement_pct != null ? `${o.achievement_pct}%` : '—'}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {o.recorded_by_user_id || '—'}
                      </td>
                      <td className="px-3 py-2 text-xs">{o.note || '—'}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" className="h-7" onClick={() => { setEditRow(o); setEditNote(o.note || ''); }} data-testid={`outcome-edit-${o.id}`}>Edit</Button>
                        <Button size="sm" variant="ghost" className="h-7 text-destructive" data-testid={`outcome-delete-${o.id}`} onClick={async () => {
                          if (!window.confirm('Delete this outcome record? This cannot be undone.')) return;
                          try { await api.delete(`/goals/outcomes/${o.id}`); toast.success('Outcome deleted'); load(); }
                          catch (e) { toast.error(e.response?.data?.error?.message || e.response?.data?.detail || 'Delete failed'); }
                        }}>Delete</Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!editRow} onOpenChange={(o) => { if (!o) setEditRow(null); }}>
        <DialogContent data-testid="outcome-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit outcome note</DialogTitle>
            <DialogDescription>Update the note recorded for this outcome.</DialogDescription>
          </DialogHeader>
          <Textarea value={editNote} onChange={(e) => setEditNote(e.target.value)} rows={4} data-testid="outcome-edit-note" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditRow(null)} disabled={busy}>Cancel</Button>
            <Button data-testid="outcome-edit-save" disabled={busy} onClick={async () => {
              setBusy(true);
              try { await api.put(`/goals/outcomes/${editRow.id}`, { note: editNote }); toast.success('Outcome updated'); setEditRow(null); load(); }
              catch (e) { toast.error(e.response?.data?.error?.message || e.response?.data?.detail || 'Update failed'); }
              finally { setBusy(false); }
            }}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
